import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Snackbar,
  Alert,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Pagination,
  Zoom
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { AuthContext } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  Timestamp,
  serverTimestamp,
  runTransaction
} from 'firebase/firestore';
import { format } from 'date-fns';
import { translateValue, jewelryTypeMap, watchMaterialMap, braceletMap } from '../../utils/productMappings';
import { pl } from 'date-fns/locale';

const AddSale = () => {
  const { currentUser, isAdmin } = useContext(AuthContext);
  
  // Stan dla produktów
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [displayedProducts, setDisplayedProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productPage, setProductPage] = useState(1);
  const [totalProductPages, setTotalProductPages] = useState(1);
  const [hoveredProduct, setHoveredProduct] = useState(null);
  const [brandFilter, setBrandFilter] = useState('all');
  const [modelFilter, setModelFilter] = useState('all');
  const productsPerPage = 10;
  
  // Stan dla klientów
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [displayedClients, setDisplayedClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [loadingClients, setLoadingClients] = useState(false);
  const [clientPage, setClientPage] = useState(1);
  const [totalClientPages, setTotalClientPages] = useState(1);
  const clientsPerPage = 10;
  
  // Stan dla formularza sprzedaży
  const [saleData, setSaleData] = useState({
    amount: '',
    payment_method: 'cash',
    location: '',
    company: '',
    notes: ''
  });
  
  // Stan dla powiadomień
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  
  // Stan dla ładowania
  const [submitting, setSubmitting] = useState(false);
  const [employees, setEmployees] = useState({});

  // Efekt pobierający produkty przy ładowaniu komponentu
  useEffect(() => {
    fetchProducts();
  }, [currentUser]);
  
  // Efekt filtrujący produkty przy zmianie kryteriów
  useEffect(() => {
    filterProducts(productSearchQuery, brandFilter, modelFilter);
    setProductPage(1); // Resetuj stronę po zmianie filtrów
  }, [productSearchQuery, brandFilter, modelFilter, products]);

  // Efekt aktualizujący wyświetlane produkty przy zmianie filtrowanych produktów lub strony
  useEffect(() => {
    updateDisplayedProducts(filteredProducts, productPage);
  }, [filteredProducts, productPage]);
  
  // Efekt pobierający klientów przy ładowaniu komponentu
  useEffect(() => {
    fetchClients();
  }, [currentUser]);
  
  // Efekt aktualizujący wyświetlanych klientów przy zmianie filtrowanych klientów
  useEffect(() => {
    updateDisplayedClients(filteredClients, clientPage);
  }, [filteredClients, clientPage]);

  // Pobieranie pracowników (do wyświetlania nazwisk przy klientach dla admina)
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const employeesMap = {};
        usersSnapshot.forEach(doc => {
          employeesMap[doc.id] = `${doc.data().firstName || ''} ${doc.data().lastName || ''}`.trim();
        });
        setEmployees(employeesMap);
      } catch (error) {
        console.error('Błąd podczas pobierania pracowników:', error);
      }
    };

    if (isAdmin) {
      fetchEmployees();
    }
  }, [isAdmin]);
  
  // Funkcja filtrująca produkty na podstawie wyszukiwania i filtrów
  const filterProducts = (searchQuery, brand, model) => {
    let filtered = products;

    // Filtrowanie po marce
    if (brand !== 'all') {
      filtered = filtered.filter(p => p.brand === brand);
    }

    // Filtrowanie po modelu
    if (model !== 'all') {
      filtered = filtered.filter(p => p.model === model);
    }

    // Filtrowanie po tekście
    if (searchQuery) {
      const lowercasedQuery = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        (p.sku && p.sku.toLowerCase().includes(lowercasedQuery)) ||
        (p.jewelry_name && p.jewelry_name.toLowerCase().includes(lowercasedQuery)) ||
        (p.reference_number && p.reference_number.toLowerCase().includes(lowercasedQuery))
      );
    }
    setFilteredProducts(filtered);
  };

  // Funkcja aktualizująca wyświetlane produkty na podstawie strony
  const updateDisplayedProducts = (products, page) => {
    const startIndex = (page - 1) * productsPerPage;
    const endIndex = startIndex + productsPerPage;
    const paginatedProducts = products.slice(startIndex, endIndex);
    
    setDisplayedProducts(paginatedProducts);
    setTotalProductPages(Math.ceil(products.length / productsPerPage));
  };
  
  // Funkcja zmieniająca stronę produktów
  const handleProductPageChange = (event, newPage) => {
    setProductPage(newPage);
    updateDisplayedProducts(filteredProducts, newPage);
  };
  
  // Funkcja filtrująca klientów na podstawie wyszukiwania
  const filterClients = (searchQuery) => {
    setClientSearchQuery(searchQuery);
    setClientPage(1); // Reset do pierwszej strony przy nowym wyszukiwaniu
    
    if (clients.length > 0) {
      if (!searchQuery) {
        // Jeśli zapytanie jest puste, pokaż wszystkich klientów
        setFilteredClients(clients);
        updateDisplayedClients(clients, 1);
        return;
      }
      
      const lowercaseQuery = searchQuery.toLowerCase();
      const terms = lowercaseQuery.split(' ').filter(term => term.trim() !== '');
      
      const filtered = clients.filter(client => {
        return terms.every(term => {
          const firstNameMatch = client.firstName && client.firstName.toLowerCase().includes(term);
          const lastNameMatch = client.lastName && client.lastName.toLowerCase().includes(term);
          const emailMatch = client.email && client.email.toLowerCase().includes(term);
          const phoneMatch = client.phone && client.phone.includes(term);
          const fullNameMatch = `${client.firstName} ${client.lastName}`.toLowerCase().includes(term);
          
          return firstNameMatch || lastNameMatch || emailMatch || phoneMatch || fullNameMatch;
        });
      });
      
      setFilteredClients(filtered);
      updateDisplayedClients(filtered, 1);
    }
  };
  
  // Funkcja aktualizująca wyświetlanych klientów na podstawie strony
  const updateDisplayedClients = (clients, page) => {
    const startIndex = (page - 1) * clientsPerPage;
    const endIndex = startIndex + clientsPerPage;
    const paginatedClients = clients.slice(startIndex, endIndex);
    
    setDisplayedClients(paginatedClients);
    setTotalClientPages(Math.ceil(clients.length / clientsPerPage));
  };
  
  // Funkcja zmieniająca stronę klientów
  const handleClientPageChange = (event, newPage) => {
    setClientPage(newPage);
    updateDisplayedClients(filteredClients, newPage);
  };

  // Funkcja pobierająca produkty z bazy danych
  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      // Pobierz tylko produkty, które są dostępne
      const q = query(collection(db, 'products'), where('status', '==', 'available'));
      
      const querySnapshot = await getDocs(q);
      const productsData = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        productsData.push({
          id: doc.id,
          ...data,
          price: data.price || 0
        });
      });
      
      // Sortuj produkty - najnowsze pierwsze
      productsData.sort((a, b) => {
        const dateA = a.created_at ? new Date(a.created_at.toDate()) : new Date(0);
        const dateB = b.created_at ? new Date(b.created_at.toDate()) : new Date(0);
        return dateB - dateA;
      });
      
      setProducts(productsData);
      setFilteredProducts(productsData);
      updateDisplayedProducts(productsData, 1);
      setLoadingProducts(false);
    } catch (error) {
      console.error('Błąd podczas pobierania produktów:', error);
      setSnackbar({
        open: true,
        message: 'Błąd podczas pobierania produktów',
        severity: 'error'
      });
      setLoadingProducts(false);
    }
  };

  // Funkcja pobierająca klientów z bazy danych
  const fetchClients = async () => {
    if (!currentUser) return;
    setLoadingClients(true);
    try {
      const clientsRef = collection(db, 'clients');
      let clientsData = [];

      if (isAdmin) {
        const q = query(clientsRef);
        const querySnapshot = await getDocs(q);
        clientsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      } else {
        // Zgodnie z nowymi, poprawnymi regułami, wykonujemy tylko jedno zapytanie.
        const q = query(clientsRef, where('assigned_to', '==', currentUser.uid));
        const querySnapshot = await getDocs(q);
        clientsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      }

      // Sortowanie klientów alfabetycznie
      clientsData.sort((a, b) => {
        const nameA = `${a.firstName || ''} ${a.lastName || ''}`.trim().toLowerCase();
        const nameB = `${b.firstName || ''} ${b.lastName || ''}`.trim().toLowerCase();
        return nameA.localeCompare(nameB);
      });

      setClients(clientsData);
      setFilteredClients(clientsData);
      updateDisplayedClients(clientsData, 1);

    } catch (error) {
      console.error("Błąd podczas pobierania klientów:", error);
      setSnackbar({
        open: true,
        message: 'Wystąpił błąd podczas ładowania klientów.',
        severity: 'error'
      });
    } finally {
      setLoadingClients(false);
    }
  };

  // Funkcja obsługująca wybór produktu
  const handleProductSelect = (product) => {
    // Upewnij się, że wszystkie pola są poprawnie ustawione
    const selectedProductWithType = {
      ...product,
      type: product.category === 'watch' ? 'watch' : 'jewelry'
    };
    
    setSelectedProduct(selectedProductWithType);
    
    // Ustaw domyślną kwotę sprzedaży na cenę produktu
    setSaleData({ 
      ...saleData, 
      amount: product.price,
      product_id: product.id,
      product_name: product.category === 'watch' ? 
        `${product.brand || ''} ${product.model || ''} ${product.case_diameter ? `${product.case_diameter}mm` : ''}`.trim() : 
        product.jewelry_name,
      product_type: product.category === 'watch' ? 'watch' : 'jewelry',
      product_price: product.price
    });
  };

  // Funkcja obsługująca wybór klienta
  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setSaleData({
      ...saleData,
      client_id: client.id,
      client_name: `${client.firstName} ${client.lastName}`.trim()
    });
  };

  // Funkcja obsługująca zmiany w formularzu sprzedaży
  const handleSaleDataChange = (e) => {
    const { name, value } = e.target;
    setSaleData({
      ...saleData,
      [name]: value
    });
  };

  // Funkcja zgłaszająca sprzedaż
  const handleSubmitSale = async () => {
    if (!selectedProduct || !selectedClient) {
      setSnackbar({
        open: true,
        message: 'Proszę wybrać produkt i klienta.',
        severity: 'warning',
      });
      return;
    }

    setSubmitting(true);

    try {
      await runTransaction(db, async (transaction) => {
        const productRef = doc(db, 'products', selectedProduct.id);
        const productDoc = await transaction.get(productRef);

        if (!productDoc.exists() || productDoc.data().status !== 'available') {
          throw new Error('Produkt nie jest już dostępny lub jest zarezerwowany.');
        }

        // 1. Zdefiniuj dane sprzedaży
        const finalAmount = parseFloat(saleData.amount) || parseFloat(selectedProduct.price);
        const productDetails = {
          brand: selectedProduct.brand || '',
          model: selectedProduct.model || '',
          sku: selectedProduct.sku || '',
          price: selectedProduct.price || 0,
          type: selectedProduct.type || '',
          jewelry_type: selectedProduct.jewelry_type || '',
          jewelry_name: selectedProduct.jewelry_name || '',
        };

        const salePayload = {
          ...saleData,
          amount: finalAmount,
          client_id: selectedClient.id,
          client_name: `${selectedClient.firstName} ${selectedClient.lastName}`,
          product_id: selectedProduct.id,
          product_data: productDetails,
          employee_id: currentUser.uid,
          user_name: currentUser.displayName || `${currentUser.firstName} ${currentUser.lastName}`,
          status: 'pending',
          transaction_date: serverTimestamp(),
          createdAt: serverTimestamp(),
          lastUpdate: serverTimestamp(),
          sale_date: saleData.sale_date ? Timestamp.fromDate(new Date(saleData.sale_date)) : serverTimestamp(),
        };

        // 2. Utwórz nowy dokument sprzedaży
        const saleRef = doc(collection(db, 'sales'));
        transaction.set(saleRef, salePayload);

        // 3. Zaktualizuj status produktu
        transaction.update(productRef, { status: 'sale_pending', saleId: saleRef.id });

        // 4. Zapisz aktywność (opcjonalnie, ale dobra praktyka)
        const activityData = {
          type: 'sale_created',
          description: `Zgłoszono sprzedaż produktu: ${productDetails.brand} ${productDetails.model}`,
          date: serverTimestamp(),
          client_id: selectedClient.id,   // POPRAWIONA NAZWA
          employee_id: currentUser.uid, // POPRAWIONA NAZWA
          saleId: saleRef.id,
        };

        const activityRef = doc(collection(db, 'activities'));
        transaction.set(activityRef, activityData);
      });

      setSnackbar({
        open: true,
        message: 'Sprzedaż została zgłoszona i oczekuje na zatwierdzenie!',
        severity: 'success',
      });

      // Reset formularza
      setSelectedProduct(null);
      setSelectedClient(null);
      setSaleData({
        amount: '',
        payment_method: 'cash',
        location: '',
        company: '',
        notes: '',
      });
      setProductSearchQuery('');
      setClientSearchQuery('');

    } catch (error) {
      console.error('Błąd podczas zgłaszania sprzedaży:', error);
      setSnackbar({
        open: true,
        message: `Wystąpił błąd: ${error.message}`,
        severity: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(price);
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Dodaj sprzedaż
      </Typography>
      
      {/* Sekcja wyboru produktu */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Wybierz produkt
        </Typography>
        
        <Box sx={{ mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Marka</InputLabel>
                  <Select
                    value={brandFilter}
                    label="Marka"
                    onChange={(e) => {
                      setBrandFilter(e.target.value);
                      setModelFilter('all'); // Resetuj model po zmianie marki
                    }}
                  >
                    <MenuItem value="all">Wszystkie marki</MenuItem>
                    {[...new Set(products.map(p => p.brand).filter(Boolean))].sort().map(brand => (
                      <MenuItem key={brand} value={brand}>{brand}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth disabled={brandFilter === 'all'}>
                  <InputLabel>Model</InputLabel>
                  <Select
                    value={modelFilter}
                    label="Model"
                    onChange={(e) => setModelFilter(e.target.value)}
                  >
                    <MenuItem value="all">Wszystkie modele</MenuItem>
                    {[...new Set(
                      products
                        .filter(p => p.brand === brandFilter && p.model)
                        .map(p => p.model)
                    )].sort().map(model => (
                      <MenuItem key={model} value={model}>{model}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Szukaj po ID, ref..."
                  value={productSearchQuery}
                  onChange={(e) => setProductSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Znaleziono produktów: {filteredProducts.length}
        </Typography>
        
        {loadingProducts ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredProducts.length === 0 ? (
          <Box sx={{ my: 2, p: 2, border: '1px dashed #ccc', borderRadius: 1, textAlign: 'center' }}>
            <Typography>Nie znaleziono produktów</Typography>
          </Box>
        ) : (
          <Box sx={{ maxHeight: '500px', overflow: 'auto', pb: 1 }}>
            <Grid container spacing={2}>
              {displayedProducts.map((product) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                  <Tooltip
                    title={
                      <Box sx={{ p: 1 }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {product.category === 'watch' 
                            ? `${product.brand} ${product.model}`
                            : product.jewelry_name}
                        </Typography>
                        <Typography variant="body2">
                          ID: {product.sku || 'N/A'}
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          Cena: {formatPrice(product.price)}
                        </Typography>
                      </Box>
                    }
                    arrow
                    placement="top"
                  >
                    <Card 
                      sx={{ 
                        cursor: 'pointer',
                        border: selectedProduct && selectedProduct.id === product.id ? '3px solid #d4af37' : '1px solid #e0e0e0',
                        transition: 'all 0.3s ease',
                        '&:hover': { transform: 'translateY(-5px)', boxShadow: 3 },
                        bgcolor: selectedProduct && selectedProduct.id === product.id ? 'rgba(212, 175, 55, 0.1)' : 'inherit',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                      onClick={() => handleProductSelect(product)}
                    >
                      {selectedProduct && selectedProduct.id === product.id && (
                        <CheckCircleIcon 
                          sx={{ 
                            position: 'absolute', 
                            top: 8, 
                            right: 8, 
                            color: '#d4af37', 
                            fontSize: 30,
                            zIndex: 2,
                            backgroundColor: 'rgba(255,255,255,0.8)',
                            borderRadius: '50%'
                          }} 
                        />
                      )}
                      <CardMedia
                        component="img"
                        image={product.images && product.images.length > 0 ? product.images[0] : 'https://via.placeholder.com/300'}
                        alt={product.category === 'watch' ? `${product.brand} ${product.model}` : product.jewelry_name}
                        sx={{ 
                          backgroundColor: '#000000',
                          aspectRatio: '1 / 1',
                          objectFit: 'contain',
                          padding: '10px'
                        }}
                      />
                      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', p: 1.5 }}>
                        <Box>
                          <Typography variant="subtitle1" component="div" fontWeight="bold" noWrap>
                            {product.category === 'watch' 
                              ? `${product.brand} ${product.model}`
                              : product.jewelry_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ID: {product.sku || 'N/A'}
                          </Typography>
                        </Box>
                        <Typography variant="h6" sx={{ mt: 1, textAlign: 'right' }}>
                          {formatPrice(product.price)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Tooltip>
                </Grid>
              ))}
            </Grid>
            {totalProductPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination 
                  count={totalProductPages} 
                  page={productPage} 
                  onChange={handleProductPageChange} 
                  color="primary" 
                  size="large"
                  showFirstButton 
                  showLastButton
                />
              </Box>
            )}
          </Box>
        )}
      </Paper>
      
      {/* Sekcja wyboru klienta */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Wybierz klienta
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <TextField
            fullWidth
            label="Wyszukaj klienta"
            variant="outlined"
            value={clientSearchQuery}
            onChange={(e) => filterClients(e.target.value)}
            onFocus={() => setFilteredClients(clients)} // Pokaż wszystkich klientów przy kliknięciu
            margin="normal"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Znaleziono klientów: {filteredClients.length}
        </Typography>
        
        {loadingClients ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredClients.length === 0 ? (
          <Box sx={{ my: 2, p: 2, border: '1px dashed #ccc', borderRadius: 1, textAlign: 'center' }}>
            <Typography>Nie znaleziono klientów</Typography>
          </Box>
        ) : (
          <Box sx={{ maxHeight: '500px', overflow: 'auto', pb: 1 }}>
            <Grid container spacing={2}>
              {displayedClients.map((client) => (
                <Grid item xs={12} sm={6} md={4} key={client.id}>
                  <Card 
                    sx={{ 
                      cursor: 'pointer',
                      border: selectedClient && selectedClient.id === client.id ? '3px solid #d4af37' : '1px solid #e0e0e0',
                      transition: 'all 0.3s ease',
                      '&:hover': { transform: 'translateY(-5px)', boxShadow: 3 },
                      bgcolor: selectedClient && selectedClient.id === client.id ? 'rgba(212, 175, 55, 0.1)' : 'inherit',
                      minHeight: '180px',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      position: 'relative'
                    }}
                    onClick={() => handleClientSelect(client)}
                  >
                    {selectedClient && selectedClient.id === client.id && (
                      <CheckCircleIcon 
                        sx={{ 
                          position: 'absolute', 
                          top: 8, 
                          right: 8, 
                          color: '#d4af37', 
                          fontSize: 30,
                          zIndex: 2,
                          backgroundColor: 'rgba(255,255,255,0.8)',
                          borderRadius: '50%'
                        }} 
                      />
                    )}
                    <CardContent sx={{ flexGrow: 1, p: 2, display: 'flex', flexDirection: 'column' }}>
                      <Box>
                        <Typography variant="subtitle1" component="div" fontWeight="bold" noWrap>
                          {client.firstName} {client.lastName}
                        </Typography>
                        {isAdmin && (
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Opiekun: {employees[client.assignedTo || client.assigned_to] || 'Nieprzypisany'}
                          </Typography>
                        )}
                      </Box>
                      <Box mt={1}>
                        {client.email && (
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {client.email}
                          </Typography>
                        )}
                        {client.phone && (
                          <Typography variant="body2" color="text.secondary">
                            {client.phone}
                          </Typography>
                        )}
                        {(client.address || client.city) && (
                          <Typography variant="body2" color="text.secondary">
                            {client.address}{client.city ? `, ${client.city}` : ''}
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
        
        {totalClientPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination 
              count={totalClientPages} 
              page={clientPage} 
              onChange={handleClientPageChange} 
              color="primary" 
            />
          </Box>
        )}
      </Paper>
      
      {/* Formularz sprzedaży */}
      {selectedProduct && selectedClient && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Szczegóły sprzedaży
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Kwota"
                name="amount"
                type="number"
                value={saleData.amount || selectedProduct.price}
                onChange={handleSaleDataChange}
                InputProps={{
                  endAdornment: <InputAdornment position="end">PLN</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Metoda płatności</InputLabel>
                <Select
                  name="payment_method"
                  value={saleData.payment_method}
                  onChange={handleSaleDataChange}
                  label="Metoda płatności"
                >
                  <MenuItem value="cash">Gotówka</MenuItem>
                  <MenuItem value="card">Karta</MenuItem>
                  <MenuItem value="transfer">Przelew</MenuItem>
                  <MenuItem value="other">Inna</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Lokalizacja</InputLabel>
                <Select
                  name="location"
                  value={saleData.location}
                  onChange={handleSaleDataChange}
                  label="Lokalizacja"
                >
                  <MenuItem value="PAN">Panorama M&M GOLD</MenuItem>
                  <MenuItem value="VVS">Panorama VVS</MenuItem>
                  <MenuItem value="MODO">Modo M&M GOLD</MenuItem>
                  <MenuItem value="KLIF">Klif M&M GOLD</MenuItem>
                  <MenuItem value="GP">Galeria Północna M&M GOLD</MenuItem>
                  <MenuItem value="SB">Stary Browar M&M GOLD</MenuItem>
                  <MenuItem value="MB">Marbella</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Firma (opcjonalnie)"
                name="company"
                value={saleData.company}
                onChange={handleSaleDataChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notatki"
                name="notes"
                value={saleData.notes}
                onChange={handleSaleDataChange}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(212, 175, 55, 0.1)', borderRadius: 1, border: '1px solid #d4af37' }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#000', fontWeight: 'bold', bgcolor: '#d4af37', p: 1, borderRadius: 1 }}>
              Podsumowanie sprzedaży
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Produkt:</Typography>
                {selectedProduct.type === 'watch' ? (
                  <>
                    <Typography variant="body2">
                      {selectedProduct.brand || 'Brak marki'} {selectedProduct.model || 'Brak modelu'} {selectedProduct.case_diameter ? `${selectedProduct.case_diameter}mm` : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedProduct.bracelet_type ? translateValue(selectedProduct.bracelet_type, braceletMap) : ''}
                      {selectedProduct.bracelet_type && selectedProduct.material ? ', ' : ''}
                      {selectedProduct.material ? translateValue(selectedProduct.material, watchMaterialMap) : ''}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="body2">
                      {selectedProduct.jewelry_type ? translateValue(selectedProduct.jewelry_type, jewelryTypeMap) : 'Biżuteria'} {selectedProduct.jewelry_name || ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ID: {selectedProduct.sku || 'N/A'}
                    </Typography>
                  </>
                )}
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Klient:</Typography>
                <Typography variant="body2">
                  {selectedClient.firstName} {selectedClient.lastName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedClient.email || selectedClient.phone || ''}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Kwota:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#d4af37' }}>
                  {formatPrice(saleData.amount || selectedProduct.price || 0)} PLN
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Metoda płatności: {saleData.payment_method === 'cash' ? 'Gotówka' : 
                    saleData.payment_method === 'card' ? 'Karta' : 
                    saleData.payment_method === 'transfer' ? 'Przelew' : 'Inna'}
                </Typography>
              </Grid>
            </Grid>
          </Box>
          
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmitSale}
              disabled={submitting || !saleData.location}
              sx={{ minWidth: 150 }}
            >
              {submitting ? <CircularProgress size={24} /> : 'Zgłoś sprzedaż'}
            </Button>
          </Box>
        </Paper>
      )}
      
      {/* Snackbar dla powiadomień */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AddSale;
